import api from '@/services/axiosApi'

const calculatePreviousPeriod = (dateRange: { start: Date; end: Date }) => {
  const periodLength = dateRange.end.getTime() - dateRange.start.getTime()
  return {
    start: new Date(dateRange.start.getTime() - periodLength),
    end: new Date(dateRange.start.getTime())
  }
}

export const fetchCoreData = async (teamPid: number, dateRange) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const params = {
    start: dateRange.start,
    end: dateRange.end,
    timezone
  }
  const previousPeriod = calculatePreviousPeriod(dateRange)
  const prevParams = {
    start: previousPeriod.start,
    end: previousPeriod.end,
    timezone
  }

  // Fire both requests in parallel — they have zero data dependencies
  // Personalization lift data (conversions/exposures) is now included in dashboardCoreData
  const [coreResult, previousPeriodResult] = await Promise.allSettled([
    api.get(`/api/${teamPid}/metrics/dashboardCoreData`, { params }),
    api.get(`/api/${teamPid}/metrics/dashboardCoreData`, { params: prevParams })
  ])

  const coreData = coreResult.status === 'fulfilled' ? coreResult.value.data : {}
  const previousPeriodData = previousPeriodResult.status === 'fulfilled' ? previousPeriodResult.value.data : {}

  return { ...coreData, previousPeriod: previousPeriodData }
}

export const fetchBuilderData = async (
  teamPid: number,
  builderParams: object,
  dateRange: { start: Date; end: Date }
) => {
  const { data: builderData } = await api.post(
    `/api/${teamPid}/metrics/queryOfferPerformance`,
    {
      ...builderParams
    },
    {
      params: { start: dateRange.start, end: dateRange.end }
    }
  )

  const buckets = [...builderData]

  if (buckets.length === 1 && buckets[0].for[0] === 'overall') {
    const emptyFor = JSON.parse(JSON.stringify(buckets[0]))
    emptyFor.for = []
    buckets.push(emptyFor)
  }

  const overallBucketIndex = buckets.findIndex(
    (b) => b.for.length === 1 && b.for[0] === 'overall'
  )
  const overallBucket = buckets.splice(overallBucketIndex, 1)[0]
  buckets.forEach((b) => {
    b.data.forEach((d) => {
      d.rate = d.denominator ? d.numerator / d.denominator : 0
      d.share = overallBucket.numerator
        ? d.numerator / overallBucket.numerator
        : 0
    })
    b.rate = b.denominator ? b.numerator / b.denominator : 0
    b.share = overallBucket.numerator
      ? b.numerator / overallBucket.numerator
      : 0
  })
  return {
    buckets,
    overallBucket,
    payloadParts: {
      ...builderParams
    }
  }
}

export const fetchLiveStreamData = async () => {
  // Implement the live stream data fetch logic here
}

export const fetchDataLastReceived = async (teamPid: number) => {
  const { data } = await api.get(`/api/${teamPid}/metrics/dataLastReceived`)

  return data
}

// Web Analytics - single endpoint for all analytics data
export const fetchWebAnalytics = async (
  teamPid: string | number,
  dateRange: { start: Date; end: Date },
  options: { sections?: string[]; limit?: number } = {}
) => {
  const params: Record<string, unknown> = {
    start: dateRange.start.toISOString(),
    end: dateRange.end.toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  if (options.sections?.length) {
    params.sections = options.sections
  }

  if (typeof options.limit === 'number') {
    params.limit = options.limit
  }

  const { data } = await api.get(`/api/${teamPid}/metrics/webAnalytics`, {
    params
  })
  return data
}

export type OptinFunnel = {
  siteVisitors: number
  anonymousVisitors: number
  formViews: number
  optedIn: number
}

// 4-stage opt-in funnel (ENG-1037): Site traffic → Anonymous → Form views → Opted in
export const fetchOptinFunnel = async (
  teamPid: number,
  dateRange: { start: Date; end: Date },
  offerIds: string[] = []
): Promise<OptinFunnel> => {
  const { data } = await api.get(`/api/${teamPid}/metrics/optinFunnel`, {
    params: {
      start: dateRange.start.toISOString(),
      end: dateRange.end.toISOString(),
      ...(offerIds.length > 0 ? { offerIds } : {})
    }
  })
  return data
}
